import { useState } from "react";
import { toast } from "sonner";
import { Archive, CalendarCheck, Clock3, Copy, Mail, MessageCircle, PhoneCall, ThumbsDown, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { SERVICE_TYPES, formatDate, whatsappNumber } from "@/lib/services";
import { ContactBadge, MESSAGE_STATUS } from "@/lib/contact";
import { useServices } from "@/context/ServicesContext";
import { NoticeButton } from "@/components/app/NoticeButton";
import { DayDialog } from "@/components/app/DayDialog";

const SLOT = { email: "E-mail", whatsapp: "WhatsApp", aviso: "Aviso" };

const Small = ({ icon: Icon, label, onClick, testid, disabled, cls = "" }) => (
  <Button size="sm" variant="outline" disabled={disabled} onClick={onClick} className={`h-8 rounded-full border-slate-700 bg-transparent px-3 text-xs ${cls}`} data-testid={testid}>
    <Icon className="mr-1 h-3.5 w-3.5" /> {label}
  </Button>
);

const MsgLine = ({ messages, id }) => {
  const entries = Object.entries(messages || {});
  if (!entries.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 text-[11px]" data-testid={`planning-msgs-${id}`}>
      {entries.map(([ch, m]) => {
        const [label, cls] = MESSAGE_STATUS[m.status] || [m.status, "text-slate-300"];
        return <span key={ch} className={cls} title={m.error || ""}>{SLOT[ch] || ch}: {label}{m.error ? ` (${m.error})` : ""}</span>;
      })}
    </div>
  );
};

export const PlanningItem = ({ s, onAction, onChanged }) => {
  const { openClient, today } = useServices();
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const overdue = s.execution_date < today;
  const askArchive = () => setArchiving({
    title: "Arquivar sem retorno", submitLabel: "Arquivar", noteLabel: "Anotação",
    description: `${s.client_name} · ${s.service_name}. Sai do calendário e do planejamento e vai para a aba Arquivados.`,
    onSubmit: async ({ note }) => { await api.post(`/services/${s.id}/archive`, { note }); toast.success(`${s.client_name} arquivado.`); onChanged(); },
  });
  const t = SERVICE_TYPES[s.service_type];
  const phone = whatsappNumber(s.phone);

  const whatsapp = async (action) => {
    const win = action === "whatsapp" ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/messages/whatsapp", { service_id: s.id, action });
      if (win) win.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(data.text)}`;
      else { await navigator.clipboard.writeText(data.text); toast.success("Mensagem copiada."); }
      onChanged();
    } catch (err) { win?.close(); toast.error(errorMessage(err)); }
  };

  const email = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/messages/email", { service_id: s.id });
      if (data.status === "enviada") toast.success(`E-mail enviado para ${data.to}.`);
      else toast.error(data.error || `Situação do e-mail: ${data.status}`);
      onChanged();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <li className="rounded-xl border border-slate-700/60 bg-slate-800/60 p-4" style={{ borderLeft: `3px solid ${t?.hex || "#94A3B8"}` }} data-testid={`planning-item-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <button onClick={() => openClient(s.client_name)} className="truncate text-left font-semibold hover:underline" data-testid={`planning-client-${s.id}`}>{s.client_name}</button>
          <div className="text-xs text-slate-400">{s.service_name} · <span className="font-mono">{formatDate(s.execution_date)}</span>{!s.client_active && " · cliente inativo"}</div>
          <div className="mt-1 text-xs text-slate-400">{[s.contact_person, s.phone, s.email].filter(Boolean).join(" · ") || "Sem telefone e e-mail"}</div>
          {s.contact_status === "adiado" && s.contact_snooze_until && <div className="text-xs text-amber-300">Voltar a falar em {formatDate(s.contact_snooze_until)}</div>}
          {s.contact_note && <div className="text-xs italic text-slate-400">“{s.contact_note}”</div>}
          <MsgLine messages={s.messages} id={s.id} />
        </div>
        <ContactBadge status={s.contact_status} testid={`planning-status-${s.id}`} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Small icon={MessageCircle} label="WhatsApp" disabled={!phone} onClick={() => whatsapp("whatsapp")} testid={`planning-whatsapp-${s.id}`} cls="border-[#25D366]/50 text-[#25D366]" />
        <Small icon={Copy} label="Copiar mensagem" onClick={() => whatsapp("copy")} testid={`planning-copy-${s.id}`} />
        <Small icon={Mail} label="Enviar e-mail" disabled={!s.email || busy} onClick={email} testid={`planning-email-${s.id}`} />
        <Small icon={UserRound} label="Ficha" onClick={() => openClient(s.client_name)} testid={`planning-open-client-${s.id}`} />
        <NoticeButton s={s} onDone={onChanged} />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Small icon={PhoneCall} label="Tentativa" onClick={() => onAction(s, "tentativa")} testid={`planning-attempt-${s.id}`} />
        <Small icon={CalendarCheck} label="Confirmou" onClick={() => onAction(s, "confirmado")} testid={`planning-confirm-${s.id}`} cls="text-emerald-300" />
        <Small icon={ThumbsDown} label="Não quer agora" onClick={() => onAction(s, "recusou")} testid={`planning-decline-${s.id}`} cls="text-rose-300" />
        <Small icon={Clock3} label="Adiar" onClick={() => onAction(s, "adiado")} testid={`planning-snooze-${s.id}`} cls="text-amber-300" />
        {overdue && <Small icon={Archive} label="Arquivar" onClick={askArchive} testid={`planning-archive-${s.id}`} cls="text-slate-300" />}
      </div>
      <DayDialog state={archiving} onClose={() => setArchiving(null)} />
    </li>
  );
};
